import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';
import { titleVariants } from './title';

/**
 * 3분할 작업대 셸 — D1 §3 "선반과 지면" / §M2 / D2 §9.
 *
 * Funding · Community · Flow 전 모듈이 공유하는 구조다.
 * 배경색이 곧 정보 위계다: **흰색은 지금 작업 중인 대상에만 준다.**
 *
 *   Shelf(회색)  목록 · 탐색
 *   Page (흰색)  본문 · 편집        ← 지금 작업 중인 것
 *   Aside(회색)  근거 · 맥락
 *
 * 반응형(D2 §9). 768px 미만은 v1 대응 범위 밖이다.
 *   md  768   1분할 스택 — 각 영역이 순서대로 쌓인다
 *   lg 1024   2분할 — 선반 + 지면. 근거는 아래 행으로 내려간다
 *   xl 1280   3분할 — 기준 뷰포트
 *
 * 각 영역은 자기 안에서 스크롤한다. 페이지 전체는 절대 늘어나지 않는다.
 */
export function Workbench({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-hidden',
        // md: 스택. lg: 선반+지면 2열, 근거는 2행. xl: 3열 1행.
        'grid grid-cols-1 grid-rows-[auto_auto_auto]',
        'lg:grid-cols-[260px_1fr] lg:grid-rows-[1fr_auto]',
        'xl:grid-cols-[260px_1fr_320px] xl:grid-rows-1',
        className,
      )}
      {...props}
    />
  );
}

/** 목록 · 탐색 패널. 회색 배경 — "선반". */
export function WorkbenchShelf({ className, ...props }: ComponentProps<'aside'>) {
  return (
    <aside
      className={cn(
        'bg-shelf border-line flex min-h-0 flex-col overflow-y-auto',
        'border-b lg:row-span-2 lg:border-r lg:border-b-0 xl:row-span-1',
        className,
      )}
      {...props}
    />
  );
}

/** 본문 · 편집 패널. 흰 배경 — "지면". 지금 작업 중인 대상. */
export function WorkbenchPage({ className, ...props }: ComponentProps<'main'>) {
  return (
    <main className={cn('bg-page flex min-h-0 flex-col overflow-hidden', className)} {...props} />
  );
}

/**
 * 근거 · 맥락 패널. 회색 배경 — "선반".
 * lg에서는 지면 아래 행으로 내려가고, xl에서 오른쪽으로 붙는다.
 */
export function WorkbenchAside({ className, ...props }: ComponentProps<'aside'>) {
  return (
    <aside
      className={cn(
        'bg-shelf border-line flex min-h-0 flex-col overflow-y-auto',
        'border-t lg:col-start-2 xl:col-start-3 xl:row-start-1 xl:border-t-0 xl:border-l',
        className,
      )}
      {...props}
    />
  );
}

/**
 * 패널 머리말.
 *
 * [D4-03] `eyebrow` prop을 없앴다. 제목 위에 12px 대문자 자간 라벨을 얹는
 * 조판은 생성된 화면의 표식이고, 한글에는 대문자가 없어서 그 자리만 영어가
 * 되어야 했다. 라벨이 하던 일(이 패널이 무엇인가)은 제목이 이미 하고 있었다.
 *
 * 위계는 크기가 만든다 — 제목 24px과 부연 12px은 두 배 차이다. 이전 조판은
 * 16px 제목에 12px 라벨이라 셋이 서로 비슷했고, 그래서 굵기로 억지로
 * 구분해야 했다(extrabold). 크기를 벌리니 굵기가 필요 없어졌다.
 */
export interface PanelHeadProps extends ComponentProps<'div'> {
  title: string;
  sub?: string;
}

export function PanelHead({ title, sub, className, ...props }: PanelHeadProps) {
  return (
    <div
      className={cn('px-card pt-card pb-row flex shrink-0 flex-col gap-1', className)}
      {...props}
    >
      <h2 className={titleVariants({ level: 'panel' })}>{title}</h2>
      {sub ? <p className="text-ink-muted text-xs">{sub}</p> : null}
    </div>
  );
}

/**
 * 패널 안의 섹션 표제 — 프로토타입이 오렌지로 조판하던 자리(`.mv-head h3`).
 *
 * [D4-03] 두 가지를 함께 걷어냈다.
 *   1. uppercase + 자간 확장 (eyebrow 관용구)
 *   2. 오렌지 텍스트 — D1 §3이 이미 "오렌지는 흐르는 것"이라 정해두었는데
 *      가만히 있는 표제에 쓰고 있었다. 오렌지는 진행률·마감에만 남긴다.
 *
 * 대신 하단 헤어라인으로 구획한다. 선으로 나누는 것이 M1이 원래 하던 방식이다.
 */
export interface SectionLabelProps extends ComponentProps<'h3'> {
  as?: 'h2' | 'h3' | 'h4';
}

export function SectionLabel({ className, children, as: Tag = 'h3', ...props }: SectionLabelProps) {
  return (
    <Tag
      className={cn(
        'border-line text-fg pb-tight shrink-0 border-b text-sm font-semibold',
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}

/**
 * 스크롤 대신 페이드아웃하는 영역 — D1 §M2.
 * 미리보기 패널처럼 "잘렸음"을 알리되 스크롤바를 만들지 않을 때 쓴다.
 */
export function FadeOut({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('mask-fade-b min-h-0 flex-1 overflow-hidden', className)} {...props} />;
}
